// CTV-800: the staff Studio's commercials and which one is ACTIVE (has the
// WaymarkProvider mounted on it).
//
// Deliberately NOT a third mode of useAiCommercials. That hook's rules are
// about the campaign: active is "the newest commercial never yet in the
// campaign", committed is derived from slot pointers, and Make another is
// gated on commitment. None of that exists here, because a studio commercial
// has no campaign to be committed to.
//
// The rule that replaces it, after the 2026-08-27 cross-write incident (a row
// mid topic-search adopted another staff member's render):
//
//   - The Studio is ONE shared list for every super_admin, but each staff
//     member's page drives at most ONE commercial: the one they are working
//     on. Nothing is active by default. The provider (and its Waymark SDK
//     iframe, which is what steals focus and what carries a Waymark login)
//     mounts only on an explicit action: Generate, Customize, Resume, Try
//     again. The one exception is a page load that finds this staff member's
//     OWN generation still in flight, which resumes on its own so a reload
//     never orphans it.
//   - Done = the row has a broadcast render. It stays in the Studio forever;
//     Customize reopens the editor on its own account and a new Finish
//     replaces the file; Download hands the file over.
//
// What IS shared is the SDK plumbing below it: one WaymarkProvider on the
// active row, and only ever one SDK instance on the page.

import React from 'react';
import { apiClient } from '../../../lib/apiClient';
import type { WaymarkInitial } from './context';
import type { WaymarkStatus } from './types';

export interface StudioCommercial {
  id: string;
  companyName: string | null;
  /** Who made it; null on rows older than the column. */
  createdByUserId: string | null;
  createdByName: string | null;
  status: WaymarkStatus;
  sourceUrl: string | null;
  topicId: string | null;
  generationRequestId: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  /** ISO time the broadcast file was first downloaded; null until then. */
  exportedAt: string | null;
  createdAt: string;
}

/** The states Waymark is still working in. Everything else is terminal. */
export const IN_PROGRESS_STATUSES: ReadonlySet<WaymarkStatus> = new Set([
  'topic_searching',
  'generating',
]);

/** Terminal failures. Try again is the only way forward from these. */
export const FAILED_STATUSES: ReadonlySet<WaymarkStatus> = new Set([
  'topic_failed',
  'generation_failed',
]);

export function isInFlight(c: Pick<StudioCommercial, 'status'> | null | undefined): boolean {
  return !!c && IN_PROGRESS_STATUSES.has(c.status);
}

/** Done: it has a broadcast file. From here it stays in the Studio for good. */
export function isDone(
  c: Pick<StudioCommercial, 'status' | 'renderedUrl'> | null | undefined,
): boolean {
  return !!c && c.status === 'ready' && !!c.renderedUrl;
}

export interface StudioCommercialsState {
  commercials: StudioCommercial[];
  activeId: string | null;
  active: StudioCommercial | null;
  isLoaded: boolean;
  isCreating: boolean;
  error: string | null;
  /** Hydration input for the provider mounted on the active commercial. */
  activeInitial: WaymarkInitial | null;
  /** Make a new studio commercial and make it active. Null on failure. */
  create: (args: { companyName: string; sourceUrl?: string }) => Promise<StudioCommercial | null>;
  /** Work on an existing row (mounts the provider on it). Null parks the page. */
  setActiveId: (id: string | null) => void;
  /** The provider persisted state; keeps the list from going stale behind it. */
  recordState: (id: string, patch: Partial<WaymarkInitial>) => void;
  /** Stamp the export locally after the server confirmed it. */
  recordExported: (id: string, exportedAt: string) => void;
  /** Soft-delete a row and drop it from the list. */
  remove: (id: string) => Promise<boolean>;
  reload: () => Promise<void>;
}

function toInitial(c: StudioCommercial): WaymarkInitial {
  return {
    status: c.status,
    sourceUrl: c.sourceUrl,
    videoId: c.videoId,
    renderedUrl: c.renderedUrl,
    errorMessage: c.errorMessage,
    topicId: c.topicId,
    generationRequestId: c.generationRequestId,
  };
}

/**
 * @param enabled false until the caller knows it is a super_admin. The page
 * redirects everyone else, and firing the list before that gate is decided
 * costs a guaranteed 401/403 on every non-staff visit.
 * @param currentUserId whose in-flight generation a page load may resume.
 */
export function useStudioCommercials(
  enabled = true,
  currentUserId: string | null = null,
): StudioCommercialsState {
  const [commercials, setCommercials] = React.useState<StudioCommercial[] | null>(null);
  const [pickedId, setPickedId] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    try {
      const res = await apiClient.get<{ commercials: StudioCommercial[] }>(
        '/admin/waymark/studio/commercials',
      );
      setCommercials(res.commercials);
    } catch (e) {
      setCommercials([]);
      setError(e instanceof Error ? e.message : 'Failed to load the Studio');
    }
  }, []);

  React.useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  // Memoized: `commercials ?? []` would be a fresh array every render and
  // would churn every memo below it.
  const list = React.useMemo(() => commercials ?? [], [commercials]);

  // Active = the row the staff member picked, while it still exists. There
  // is no fallback to the newest: that made every visitor's page boot an
  // SDK on someone else's commercial.
  const activeId = React.useMemo<string | null>(() => {
    if (pickedId && list.some((c) => c.id === pickedId)) return pickedId;
    return null;
  }, [pickedId, list]);

  // Once per load: pick up this staff member's OWN generation if one is
  // still in flight, so a reload (or a closed-and-reopened tab) resumes it
  // rather than leaving it stranded. Someone else's in-flight row is theirs
  // to resume, from the library.
  const autoResumedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoResumedRef.current || commercials === null) return;
    autoResumedRef.current = true;
    if (pickedId || !currentUserId) return;
    const mine = commercials.find(
      (c) => c.createdByUserId === currentUserId && isInFlight(c),
    );
    if (mine) setPickedId(mine.id);
  }, [commercials, pickedId, currentUserId]);

  const active = React.useMemo(
    () => list.find((c) => c.id === activeId) ?? null,
    [list, activeId],
  );

  // The provider's hydration input. `active` only changes identity when the
  // list rebuilds, which is exactly when one of these fields moved.
  const activeInitial = React.useMemo<WaymarkInitial | null>(
    () => (active ? toInitial(active) : null),
    [active],
  );

  const create = React.useCallback(
    async ({ companyName, sourceUrl }: { companyName: string; sourceUrl?: string }) => {
      setIsCreating(true);
      setError(null);
      try {
        const res = await apiClient.post<StudioCommercial>(
          '/admin/waymark/studio/commercials',
          { companyName, ...(sourceUrl ? { sourceUrl } : {}) },
        );
        setCommercials((prev) => [res, ...(prev ?? [])]);
        setPickedId(res.id);
        return res;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start the commercial');
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [],
  );

  const recordState = React.useCallback((id: string, patch: Partial<WaymarkInitial>) => {
    setCommercials((prev) =>
      (prev ?? []).map((c) =>
        c.id === id
          ? {
              ...c,
              status: patch.status ?? c.status,
              sourceUrl: patch.sourceUrl !== undefined ? patch.sourceUrl : c.sourceUrl,
              topicId: patch.topicId !== undefined ? (patch.topicId ?? null) : c.topicId,
              generationRequestId:
                patch.generationRequestId !== undefined
                  ? (patch.generationRequestId ?? null)
                  : c.generationRequestId,
              videoId: patch.videoId !== undefined ? patch.videoId : c.videoId,
              renderedUrl: patch.renderedUrl !== undefined ? patch.renderedUrl : c.renderedUrl,
              errorMessage:
                patch.errorMessage !== undefined ? patch.errorMessage : c.errorMessage,
            }
          : c,
      ),
    );
  }, []);

  const recordExported = React.useCallback((id: string, exportedAt: string) => {
    setCommercials((prev) =>
      // Never move an existing stamp: exported_at is set once server-side and
      // the count feeds a vendor true-up.
      (prev ?? []).map((c) => (c.id === id && !c.exportedAt ? { ...c, exportedAt } : c)),
    );
  }, []);

  const remove = React.useCallback(async (id: string) => {
    setError(null);
    try {
      await apiClient.delete(`/admin/waymark/studio/commercials/${id}`);
      setCommercials((prev) => (prev ?? []).filter((c) => c.id !== id));
      setPickedId((prev) => (prev === id ? null : prev));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the commercial');
      return false;
    }
  }, []);

  return {
    commercials: list,
    activeId,
    active,
    isLoaded: enabled && commercials !== null,
    isCreating,
    error,
    activeInitial,
    create,
    setActiveId: setPickedId,
    recordState,
    recordExported,
    remove,
    reload,
  };
}
